import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { keys } from '../lib/query-client.js';
import { hasLiveTargets, type ConfirmPlan, type PostDetail, type TargetEvent } from './types.js';
import type { Platform, PostStatus, TargetStatus } from '@dintiko/shared';

export interface PostListFilters {
  status?: PostStatus | undefined;
  account?: string | undefined;
  q?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  /** Lets the same object serve as a query-string bag and a cache key. */
  [key: string]: string | undefined;
}

/** Matches `PostSummary` in apps/api/src/posts/types.ts, dates as ISO strings. */
export interface PostListItem {
  id: string;
  hook: string;
  status: PostStatus;
  scheduledAt: string | null;
  updatedAt: string;
  targets: {
    accountId: string;
    platform: Platform;
    handle: string;
    status: TargetStatus;
  }[];
}

export function usePosts(filters: PostListFilters = {}): UseQueryResult<PostListItem[]> {
  return useQuery({
    queryKey: keys.posts(filters),
    queryFn: () => api.get<{ posts: PostListItem[] }>('/posts', filters).then((r) => r.posts),
    // The dashboard and the posts list both show live work; 15 s matches
    // PLAN §13.4 and stops when nothing is in flight.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const live = data.some((post) => post.status === 'publishing');
      return live ? 15_000 : false;
    },
  });
}

/**
 * One post and its targets, polled every 5 s while any target is still
 * working and not a moment longer (PLAN §13.4).
 */
export function usePost(id: string | undefined): UseQueryResult<PostDetail> {
  return useQuery({
    queryKey: keys.post(id ?? ''),
    enabled: Boolean(id),
    queryFn: () => api.get<PostDetail>(`/posts/${String(id)}`),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return hasLiveTargets(data.targets) ? 5_000 : false;
    },
  });
}

export function useTargetEvents(targetId: string | undefined, live: boolean) {
  return useQuery({
    queryKey: keys.targetEvents(targetId ?? ''),
    enabled: Boolean(targetId),
    queryFn: () =>
      api
        .get<{ events: TargetEvent[] }>(`/targets/${String(targetId)}/events`)
        .then((r) => r.events),
    refetchInterval: live ? 5_000 : false,
  });
}

export interface CreatePostInput {
  hook?: string;
  body?: string | null;
  cta?: string | null;
  link?: string | null;
  hashtags?: string[];
  mediaIds?: string[];
  accountIds?: string[];
}

export function useCreatePost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePostInput) => api.post<PostDetail>('/posts', input),
    onSuccess(result) {
      client.setQueryData(keys.post(result.post.id), result);
      void client.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export type PatchPostInput = CreatePostInput & {
  scheduledAt?: string | null;
  staggerMinutes?: number;
  targets?: {
    accountId: string;
    overrideTitle?: string | null;
    overrideText?: string | null;
    settings?: Record<string, unknown>;
  }[];
};

/**
 * The composer's autosave (PLAN §13.3). Deliberately not optimistic: the
 * server regenerates per-account captions on every patch, and guessing that
 * result in the browser would make the tabs flicker between two versions.
 */
export function usePatchPost(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PatchPostInput) => api.patch<PostDetail>(`/posts/${id}`, input),
    onSuccess(result) {
      client.setQueryData(keys.post(id), result);
    },
  });
}

export function usePreflight(id: string) {
  return useMutation({
    mutationFn: (input: {
      mode?: 'now' | 'schedule';
      scheduledAt?: string | null;
      staggerMinutes?: number;
    }) => api.post<ConfirmPlan>(`/posts/${id}/preflight`, input),
  });
}

export function useConfirmPost(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      mode: 'now' | 'schedule';
      scheduledAt?: string | null;
      staggerMinutes?: number;
    }) => api.post<ConfirmPlan>(`/posts/${id}/confirm`, input),
    onSuccess() {
      void client.invalidateQueries({ queryKey: keys.post(id) });
      void client.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useUnschedulePost(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<PostDetail>(`/posts/${id}/unschedule`),
    onSuccess(result) {
      client.setQueryData(keys.post(id), result);
      void client.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

/**
 * Unschedule, with the post named at call time rather than at hook time.
 *
 * `useUnschedulePost(id)` above is bound to one post, which is right on the
 * detail screen and useless to the list, where the ids are not known until
 * something is selected. Hooks cannot be called in a loop, so the bulk path
 * needs the id as a variable.
 */
export function useUnscheduleAnyPost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<PostDetail>(`/posts/${id}/unschedule`),
    onSuccess(result) {
      client.setQueryData(keys.post(result.post.id), result);
      void client.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useDuplicatePost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<PostDetail>(`/posts/${id}/duplicate`),
    onSuccess(result) {
      client.setQueryData(keys.post(result.post.id), result);
      void client.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useDeletePost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/posts/${id}`),
    onSuccess() {
      void client.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useRetryTarget(postId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: string | { targetId: string; confirmedNotSent?: boolean }) => {
      const { targetId, confirmedNotSent } =
        typeof input === 'string' ? { targetId: input, confirmedNotSent: undefined } : input;
      // "I looked, it is not there", sent with the retry rather than saved
      // first — so a licence to post twice cannot outlive the retry it was
      // granted for (D-035). The server picks the platform's own key.
      return api.post<void>(
        `/targets/${targetId}/retry`,
        confirmedNotSent ? { confirmedNotSent: true } : {},
      );
    },
    onSuccess() {
      void client.invalidateQueries({ queryKey: keys.post(postId) });
    },
  });
}

export function useCancelTarget(postId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (targetId: string) => api.post<void>(`/targets/${targetId}/cancel`),
    onSuccess() {
      void client.invalidateQueries({ queryKey: keys.post(postId) });
    },
  });
}
